import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { CheckCircle2, ShieldCheck, FileText, ArrowUp, Globe, Clock } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import {
  getContent,
  detectLocale,
  buildToc,
  LOCALE_ORDER,
  locales,
  STORAGE_KEY,
} from "@/content";

const BRAND = "#E3000F";

/* ---------------- Language Switcher ---------------- */
const LanguageSwitcher = ({ locale, onChange }) => (
  <div
    data-testid="language-switcher"
    className="flex items-center gap-1 rounded-full border border-[#E5E7EB] bg-white p-1"
  >
    <Globe className="w-4 h-4 text-[#525252] ml-1.5 mr-0.5" />
    {LOCALE_ORDER.map((code) => {
      const active = code === locale;
      return (
        <button
          key={code}
          data-testid={`lang-${code}`}
          onClick={() => onChange(code)}
          className={`px-2.5 py-1 text-xs font-semibold rounded-full uppercase transition-all ${
            active ? "text-white" : "text-[#525252] hover:text-[#0A0A0A]"
          }`}
          style={active ? { backgroundColor: BRAND } : undefined}
        >
          {code}
        </button>
      );
    })}
  </div>
);

/* ---------------- Header ---------------- */
const Header = ({ ui, locale, onLangChange }) => (
  <header
    data-testid="site-header"
    className="sticky top-0 z-50 w-full bg-white/90 backdrop-blur-xl border-b border-[#E5E7EB]"
  >
    <div className="max-w-6xl mx-auto px-4 md:px-8 h-16 flex items-center gap-3">
      <div className="flex items-center gap-3" data-testid="lojack-logo">
        <img
          src="/lojack-logo.png"
          alt="LoJack"
          className="h-9 w-auto rounded-md"
        />
        <span className="hidden sm:inline-block h-5 w-px bg-[#E5E7EB]" />
        <span className="hidden sm:inline-block text-sm text-[#525252] font-medium">
          {ui.legalDocuments}
        </span>
      </div>
      <div className="ml-auto">
        <LanguageSwitcher locale={locale} onChange={onLangChange} />
      </div>
    </div>
  </header>
);

/* ---------------- Table of Contents ---------------- */
const TableOfContents = ({ activeId, tocItems, title }) => {
  const handleClick = (e, id) => {
    e.preventDefault();
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <nav
      data-testid="table-of-contents"
      className="toc-scroll flex flex-col space-y-2 border-l border-[#E5E7EB] max-h-[calc(100vh-10rem)] overflow-y-auto pr-2"
    >
      <p className="pl-4 mb-2 text-xs font-semibold uppercase tracking-widest text-[#9CA3AF]">
        {title}
      </p>
      {tocItems.map((item) => {
        const isActive = activeId === item.id;
        return (
          <a
            key={item.id}
            href={`#${item.id}`}
            data-testid={`toc-link-${item.id}`}
            onClick={(e) => handleClick(e, item.id)}
            className={`-ml-px pl-4 py-1 text-sm leading-snug border-l-2 transition-all duration-200 ${
              isActive
                ? "font-semibold"
                : "border-transparent text-[#525252] hover:border-gray-300 hover:text-[#0A0A0A]"
            }`}
            style={isActive ? { borderColor: BRAND, color: BRAND } : undefined}
          >
            {item.label}
          </a>
        );
      })}
    </nav>
  );
};

/* ---------------- Renderers ---------------- */
const Table = ({ table }) => (
  <div className="overflow-x-auto my-6 rounded-lg border border-[#E5E7EB]">
    <table className="w-full text-sm text-left border-collapse">
      <thead>
        <tr className="bg-[#F3F4F6]">
          {table.headers.map((h, i) => (
            <th
              key={i}
              className="p-3 border-b border-[#E5E7EB] font-semibold text-[#0A0A0A] whitespace-nowrap"
            >
              {h}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {table.rows.map((row, ri) => (
          <tr key={ri} className="align-top">
            {row.map((cell, ci) => (
              <td
                key={ci}
                className={`p-3 border-b border-[#E5E7EB] text-[#525252] ${
                  cell === "✓" ? "text-center font-semibold" : ""
                }`}
                style={cell === "✓" ? { color: BRAND } : undefined}
              >
                {cell}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const Paragraphs = ({ items }) =>
  items.map((p, i) => (
    <p key={i} className="text-[15px] leading-relaxed text-[#374151] mb-4">
      {p}
    </p>
  ));

const Section = ({ id, number, title, paragraphs, subsections }) => (
  <section
    id={id}
    data-testid={`legal-section-${id}`}
    className="scroll-anchor mb-14"
  >
    <div className="flex items-baseline gap-3 mb-5">
      {number && (
        <span
          className="font-heading text-sm font-bold uppercase tracking-wider whitespace-nowrap"
          style={{ color: BRAND }}
        >
          {number}
        </span>
      )}
      <h2 className="font-heading text-2xl md:text-3xl font-semibold tracking-tight text-[#0A0A0A]">
        {title}
      </h2>
    </div>
    {paragraphs && <Paragraphs items={paragraphs} />}
    {subsections &&
      subsections.map((sub, i) => (
        <div key={i} className="mt-6">
          {sub.heading && (
            <h3 className="font-heading text-lg font-medium text-[#0A0A0A] mt-8 mb-3">
              {sub.heading}
            </h3>
          )}
          {sub.paragraphs && <Paragraphs items={sub.paragraphs} />}
          {sub.table && <Table table={sub.table} />}
        </div>
      ))}
  </section>
);

/* ---------------- Success State ---------------- */
const SuccessState = ({ ui }) => {
  const [seconds, setSeconds] = useState(5);
  const [closed, setClosed] = useState(false);

  useEffect(() => {
    if (seconds <= 0) {
      // Attempt to close the tab. Browsers only allow this for script-opened
      // windows; if blocked, we show a fallback message instead.
      setClosed(true);
      window.close();
      return;
    }
    const t = setTimeout(() => setSeconds((s) => s - 1), 1000);
    return () => clearTimeout(t);
  }, [seconds]);

  return (
    <motion.div
      data-testid="success-confirmation"
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className="flex flex-col items-center justify-center min-h-[70vh] text-center px-4"
    >
      <motion.div
        initial={{ scale: 0.7, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ delay: 0.1, type: "spring", stiffness: 200, damping: 15 }}
      >
        <CheckCircle2 className="w-20 h-20 mb-6 text-[#16A34A]" />
      </motion.div>
      <h1 className="font-heading text-3xl md:text-4xl font-semibold text-[#0A0A0A] mb-4">
        {ui.successTitle}
      </h1>
      <p className="text-lg text-[#525252] max-w-md">{ui.successBody}</p>
      <p className="mt-6 text-sm text-[#9CA3AF]">
        {ui.acceptedOn} {new Date().toLocaleString()}
      </p>
      <div
        data-testid="closing-notice"
        className="mt-8 flex items-center gap-2 text-sm font-medium"
        style={{ color: BRAND }}
      >
        {!closed ? (
          <>
            <Clock className="w-4 h-4" />
            <span>{ui.closingNotice.replace("{n}", seconds)}</span>
          </>
        ) : (
          <span className="text-[#525252]">{ui.closedFallback}</span>
        )}
      </div>
    </motion.div>
  );
};

/* ---------------- Acceptance Bar ---------------- */
const AcceptanceBar = ({ checked, setChecked, onSubmit, error, ui }) => (
  <div
    data-testid="acceptance-bar"
    className="fixed bottom-0 left-0 w-full bg-white border-t border-[#E5E7EB] z-40"
    style={{ boxShadow: "0 -8px 30px rgba(0,0,0,0.06)" }}
  >
    <div className="max-w-5xl mx-auto px-4 md:px-8 py-4 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
      <label
        htmlFor="accept"
        className="flex items-start gap-3 cursor-pointer select-none"
      >
        <Checkbox
          id="accept"
          data-testid="acceptance-checkbox"
          checked={checked}
          onCheckedChange={(v) => setChecked(Boolean(v))}
          className="mt-0.5 h-5 w-5 border-2 border-gray-300 rounded-[4px] data-[state=checked]:bg-[#E3000F] data-[state=checked]:border-[#E3000F] data-[state=checked]:text-white"
        />
        <span className="text-sm text-[#0A0A0A] font-medium leading-snug max-w-xl">
          {ui.acceptLabel}
          <span style={{ color: BRAND }}> *</span>
          {error && (
            <span
              data-testid="acceptance-error"
              className="block text-[#E3000F] font-normal mt-1"
            >
              {ui.acceptError}
            </span>
          )}
        </span>
      </label>
      <button
        data-testid="accept-submit-button"
        onClick={onSubmit}
        className="w-full md:w-auto px-8 py-3 text-white font-semibold rounded-md shadow-md transition-all duration-200 hover:brightness-90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#E3000F]/50 flex-shrink-0"
        style={{ backgroundColor: BRAND }}
      >
        {ui.acceptButton}
      </button>
    </div>
  </div>
);

/* ---------------- Page ---------------- */
export default function LegalPage() {
  const [locale, setLocale] = useState(() => detectLocale());
  const [checked, setChecked] = useState(false);
  const [error, setError] = useState(false);
  const [accepted, setAccepted] = useState(false);
  const [activeId, setActiveId] = useState("preamble");
  const [showTop, setShowTop] = useState(false);
  const sectionsRef = useRef([]);

  const c = getContent(locale);
  const { documentMeta, preamble, articles, annexes, ui } = c;
  const tocItems = buildToc(c);
  const allSections = [preamble, ...articles, ...annexes];

  useEffect(() => {
    document.documentElement.lang = locale;
    window.localStorage.setItem(STORAGE_KEY, locale);
    document.title = `LoJack — ${ui.customerAgreement}`;
  }, [locale, ui.customerAgreement]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) setActiveId(entry.target.id);
        });
      },
      { rootMargin: "-20% 0px -70% 0px", threshold: 0 }
    );
    const els = sectionsRef.current.filter(Boolean);
    els.forEach((el) => observer.observe(el));
    const onScroll = () => setShowTop(window.scrollY > 600);
    window.addEventListener("scroll", onScroll);
    return () => {
      els.forEach((el) => observer.unobserve(el));
      window.removeEventListener("scroll", onScroll);
    };
  }, [accepted, locale]);

  const handleSubmit = () => {
    if (!checked) {
      setError(true);
      return;
    }
    setError(false);
    setAccepted(true);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <div className="min-h-screen bg-[#F9FAFB]">
      <Header ui={ui} locale={locale} onLangChange={setLocale} />

      <AnimatePresence mode="wait">
        {accepted ? (
          <SuccessState key="success" ui={ui} />
        ) : (
          <div key={`content-${locale}`}>
            {/* Hero */}
            <section className="bg-white border-b border-[#E5E7EB]">
              <div className="max-w-6xl mx-auto px-4 md:px-8 py-14 md:py-20">
                <div
                  className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-semibold mb-6"
                  style={{ backgroundColor: "#FDECEC", color: BRAND }}
                >
                  <ShieldCheck className="w-4 h-4" />
                  {ui.customerAgreement}
                </div>
                <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-semibold tracking-tight text-[#0A0A0A] max-w-4xl">
                  {documentMeta.title}
                </h1>
                <p className="mt-6 text-base md:text-lg text-[#525252] max-w-3xl leading-relaxed">
                  {documentMeta.subtitle}
                </p>
                <div className="mt-6 flex items-center gap-2 text-xs text-[#9CA3AF]">
                  <FileText className="w-4 h-4" />
                  <span>{documentMeta.reference}</span>
                </div>
              </div>
            </section>

            {/* Body */}
            <div className="max-w-6xl mx-auto px-4 md:px-8 grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-16 items-start py-12 md:py-20">
              <aside className="hidden md:block md:col-span-4 lg:col-span-3 sticky top-24">
                <TableOfContents
                  activeId={activeId}
                  tocItems={tocItems}
                  title={ui.contents}
                />
              </aside>

              <main className="col-span-1 md:col-span-8 lg:col-span-9 max-w-3xl pb-40">
                {allSections.map((s, i) => (
                  <div key={s.id} ref={(el) => (sectionsRef.current[i] = el)}>
                    <Section {...s} />
                  </div>
                ))}
              </main>
            </div>
          </div>
        )}
      </AnimatePresence>

      {!accepted && (
        <AcceptanceBar
          ui={ui}
          checked={checked}
          setChecked={(v) => {
            setChecked(v);
            if (v) setError(false);
          }}
          onSubmit={handleSubmit}
          error={error}
        />
      )}

      {showTop && !accepted && (
        <button
          data-testid="back-to-top"
          onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
          className="fixed bottom-24 right-6 z-40 w-11 h-11 rounded-full bg-[#0A0A0A] text-white flex items-center justify-center shadow-lg hover:bg-[#333] transition-colors"
          aria-label={ui.backToTop}
        >
          <ArrowUp className="w-5 h-5" />
        </button>
      )}
    </div>
  );
}
